import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import cn from 'classnames';
import ConfigContext from '../../../contexts/ConfigContext';

// Components
import Meta from '../../shared/Meta';
import CostEstimator from '../content/CostEstimator';
import FeaturedTrucks from '../FeaturedTrucks';
import CtaImage from '../content/CtaImage';
import LogoList from '../content/LogoList';
import AnyEvent from '../content/AnyEvent';
import WhyBookWithUs from '../content/WhyBookWithUs';
import FeaturedCuisines from '../FeaturedCuisines';
import HowItWorks from '../content/HowItWorks';
import CateringCta from '../content/CateringCta';
import TripleCta from '../content/TripleCta';
import Testimonials from '../content/Testimonials';
import BurgerFaq from '../faqs/BurgerFaq';

import styles from '../../../../stylesheets/public/layouts/AboutPage.module.postcss';

function BurgerCatering() {
  const { vehicle_type, city_name, site_title } = useContext(ConfigContext);
  const vehicleType = vehicle_type.toLowerCase();

  const featuredTrucks = { food_categories: 'hamburgers' };

  return (
    <div className='CateringPage'>
      <Meta
        title={`Burger Food ${vehicle_type} Catering`}
        type='website'
        canonical='/food-truck-catering/burger-catering'
        image='https://static.seattlefoodtruck.com/photos/burger1.webp'
        description={`Book burger food ${vehicleType} catering in ${city_name} for corporate events, weddings, and parties. Get free quotes from top burger ${vehicleType}s.`}
        structuredData={{
          cateringService: {
            name: `Burger Food ${vehicle_type} Catering in ${city_name}`,
            description: `Book burger food ${vehicleType} catering in ${city_name} for corporate events, weddings, and parties.`,
            url: '/food-truck-catering/burger-catering',
            serviceType: 'Burger Food Truck Catering',
          },
          breadcrumbs: {
            listItems: [
              { '@type': 'ListItem', position: 2, name: 'Catering', item: '/catering' },
              { '@type': 'ListItem', position: 3, name: 'Burger Catering', item: '/food-truck-catering/burger-catering' }
            ]
          }
        }}
      />

      <div className='Section'>
        <div className={cn(styles.Cta, styles.Cta__reversed)}>
          <CtaImage image='https://static.seattlefoodtruck.com/photos/burger1.webp' noLazy isResponsive />
          <div className={styles.Cta_copy}>
            <h1>Burger Food {vehicle_type} Catering for Your Event</h1>
            <p>Juicy, sizzling, and stacked just the way you like it — nothing satisfies like a gourmet burger! Whether you&#39;re planning a <Link to='/food-truck-catering/corporate-catering'>corporate event</Link>, <Link to='/food-truck-catering/wedding-catering'>wedding</Link>, <Link to='/food-truck-catering/birthday-parties'>birthday party</Link>, or <Link to='/food-truck-catering/community-events'>festival</Link>, a burger food {vehicleType} is always a hit. From classic cheeseburgers to gourmet wagyu patties, plant-based options, and creative toppings, {site_title} makes it effortless to find and book the best burger {vehicleType}s in town. Simply tell us about your event, receive tailored quotes, and secure your perfect match — it&#39;s that easy!</p>
            <p>
              <Link to='/catering/register' className='Button Button--mobile-w100'>Start Your Burger Catering Request</Link>
            </p>
          </div>
        </div>
      </div>

      <section className='Section'>
        <LogoList title='Trusted by leading companies' titleClassName='u-textCenter' className='u-mt1' />
      </section>

      <TripleCta
        cuisine='burger'
        image='https://static.seattlefoodtruck.com/photos/burger2.webp'
        optionsText='Options include classic beef, wagyu, chicken, and plant-based burgers.'
      />

      <FeaturedTrucks title={`Featured Burger Food ${vehicle_type}s`} options={featuredTrucks} />

      <WhyBookWithUs title={`Why Book a Burger Food ${vehicle_type} for Catering?`} />

      <section className='Section--extraPadding'>
        <h2 className='u-textCenter u-mb3'>What Makes Us Different?</h2>
        <div className={cn(styles.Cta, styles.Cta__reversed)}>
          <CtaImage image='https://static.seattlefoodtruck.com/photos/burger3.webp' isResponsive />
          <div className={styles.Cta_copy}>
            <h4>The Best Burgers, Made Fresh On-Site</h4>
            <p>
              {site_title} connects you with the best burger food {vehicleType}s in the city, serving up high-quality, made-to-order burgers with fresh ingredients. Whether you&#39;re craving a juicy double-stacked cheeseburger, a smoky BBQ bacon burger, or a gourmet truffle aioli wagyu burger, our vendors take burger perfection seriously. Many of our {vehicleType}s use locally sourced meats, house-made sauces, and artisan buns to deliver an elevated burger experience.
            </p>
          </div>
        </div>
      </section>

      <section className='Section--extraPadding'>
        <div className={styles.Cta}>
          <CtaImage image='https://static.seattlefoodtruck.com/photos/burger4.webp' isResponsive />
          <div className={styles.Cta_copy}>
            <h4>Effortless Burger Catering for Any Event</h4>
            <p>
              Booking a burger {vehicleType} should be as easy as ordering one. Our streamlined process allows you to enter your event details, receive multiple quotes from verified burger vendors, and book in minutes. Whether you&#39;re feeding 50 people at a corporate lunch or 500 guests at a festival, our platform ensures a smooth and reliable experience.
            </p>
          </div>
        </div>
      </section>

      <section className='Section--extraPadding'>
        <div className={cn(styles.Cta, styles.Cta__reversed)}>
          <CtaImage image='https://static.seattlefoodtruck.com/photos/burger5.webp' isResponsive />
          <div className={styles.Cta_copy}>
            <h4>Customizable Menus & Dietary Options</h4>
            <p>
              Need gluten-free buns or a fully plant-based burger bar? Want a build-your-own burger station with an array of toppings? Our burger food {vehicleType}s can customize menus to fit your guests&#39; preferences, including vegetarian, vegan, and allergy-friendly options.
            </p>
          </div>
        </div>
      </section>

      <HowItWorks />

      <AnyEvent title={`Burger Food ${vehicle_type} Catering for Any Event`} />

      <Testimonials title="Join Thousands Who've Booked Burger Catering With Us!" />

      <CostEstimator title='Burger Catering Cost Estimator' />

      <section className='Section--extraPadding'>
        <FeaturedCuisines />
      </section>

      <section className='Section--extraPadding'>
        <BurgerFaq />
      </section>

      <section className='Section--extraPadding u-pt2'>
        <CateringCta />
      </section>
    </div>
  );
}

export default BurgerCatering;
